import { useEffect, useMemo, useState } from 'react';

import { createFilterBuilder, extractYear } from '@jebel/utils';
import { POST_REPORT_STATUS } from '@jebel/constants';

import { useSearchContext } from 'shared/features/search';
import {
  LegacyFeedPostFilter,
  LegacyFeedYearFilter,
  LegacyTimelineYearsQuery,
  SortOrder,
  useLegacyTimelineYearsQuery,
} from 'shared/graphql';
import { sendToSentry } from 'shared/utils/sentry';

import { LegacyFilterType } from '../types';
import { useSchoolConfiguration } from 'shared/hooks';

const YEARS_PER_PAGE = 6;

type Options = Parameters<typeof useLegacyTimelineYearsQuery>[0] & {
  /** @default true */
  withPagination?: boolean;
};

export function useLegacyTimelineYears(options?: Options) {
  const [refreshing, setRefreshing] = useState(false);
  const [direction, setDirection] = useState<SortOrder>('DESC');

  const { data: school } = useSchoolConfiguration();
  const { sortOption, filter: filterOptions } = useSearchContext<LegacyFilterType>();

  const withPagination = options?.withPagination ?? true;

  const filter = useMemo(() => {
    const startYear = filterOptions?.legacyStartDate
      ? extractYear(filterOptions.legacyStartDate)
      : undefined;

    const endYear = filterOptions.legacyEndDate
      ? extractYear(filterOptions.legacyEndDate)
      : undefined;

    const filterPosts = createFilterBuilder<LegacyFeedPostFilter>()
      .and({
        id: { is_not_empty: true },
        // Ensure only allowed post are being displayed.
        // https://8base-dev.atlassian.net/browse/JEB-1580?focusedCommentId=45490
        reportStatus: { equals: POST_REPORT_STATUS.postAllowed },
      })
      .and({
        // Ensure only post that have been posted are being displayed.
        // https://github.com/8base-services/jebel/issues/1449
        OR: [{ postDate: { lte: new Date().toISOString() } }, { postDate: { is_empty: true } }],
      })
      .build();

    const filter = createFilterBuilder<LegacyFeedYearFilter>({
      legacyMonths: {
        // Check whatever have a post.
        some: {
          legacyPosts: {
            some: filterPosts,
          },
        },
      },
    });

    if (school) {
      // https://github.com/jebelapp/jebel/issues/1571
      filter.and({ school: { id: { equals: school.id } } });
    }

    if (startYear && endYear) {
      filter.and({
        AND: [{ legacyYear: { gte: startYear } }, { legacyYear: { lte: endYear } }],
      });
    }

    return filter.build();
  }, [filterOptions, school]);

  useEffect(() => {
    const direction = sortOption.legacyDate as SortOrder;

    if (direction) {
      setDirection(direction);
    }
  }, [sortOption]);

  const {
    data: response,
    loading,
    fetchMore: next,
  } = useLegacyTimelineYearsQuery({
    variables: {
      filter,
      first: withPagination ? YEARS_PER_PAGE : undefined,
      sort: [{ legacyYear: direction }],
    },
  });

  const data = response?.years.items ?? [];
  const count = response?.years.count ?? 0;

  const hasMore = data.length < count;

  const fetchMore = async () => {
    setRefreshing(true);

    try {
      await next({
        variables: { skip: data.length },

        updateQuery(prev, { fetchMoreResult: curr }) {
          const newest = curr?.years.items ?? [];
          const oldests = prev?.years.items ?? [];

          const response: LegacyTimelineYearsQuery = {
            ...curr,

            years: {
              count: curr?.years.count ?? count,
              items: [...oldests, ...newest],
            },
          };

          return response;
        },
      });
    } catch (err) {
      sendToSentry(err);
    } finally {
      setRefreshing(false);
    }
  };

  return {
    data,
    loading,
    refreshing,
    hasMore,

    fetchMore,
  };
}
